import { useEffect, useState, useRef } from "react";

/* Pauses interval-driven animations when:
   1. The browser tab is hidden (Page Visibility API), or
   2. The provided element is fully off-screen (IntersectionObserver, default rootMargin=200px).
   Returns { ref, active } — attach `ref` to the section, gate intervals on `active`. */
export const useActiveOnScreen = ({ rootMargin = "200px" } = {}) => {
    const ref = useRef(null);
    const [onScreen, setOnScreen] = useState(true);
    const [tabVisible, setTabVisible] = useState(() =>
        typeof document === "undefined" ? true : !document.hidden
    );

    // Tab visibility
    useEffect(() => {
        const onVis = () => setTabVisible(!document.hidden);
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, []);

    // Section in viewport
    useEffect(() => {
        const node = ref.current;
        if (!node || typeof IntersectionObserver === "undefined") return undefined;
        const io = new IntersectionObserver(
            ([entry]) => setOnScreen(entry.isIntersecting),
            { rootMargin }
        );
        io.observe(node);
        return () => io.disconnect();
    }, [rootMargin]);

    return { ref, active: onScreen && tabVisible };
};
